import React from "react";
import Seo from "@/components/Seo";

export default function PolitiqueConfidentialitePage() {
  return (
    <div className="container-x py-16 max-w-3xl" data-testid="politique-confidentialite-page">
      <Seo
        title="Politique de Confidentialité"
        description="Politique de confidentialité et protection des données personnelles du site Recette-Diabete, conforme au RGPD."
        path="/politique-confidentialite"
      />
      <p className="overline mb-3">RGPD &amp; protection des données</p>
      <h1 className="font-serif text-4xl md:text-5xl mb-8">Politique de Confidentialité</h1>
      <p className="text-sm text-[hsl(var(--text-secondary))] mb-8">Dernière mise à jour : février 2026</p>

      <div className="prose max-w-none space-y-6 text-[hsl(var(--text-secondary))]">
        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">1. Responsable du traitement</h2>
          <p>
            Le responsable du traitement des données personnelles est la société{" "}
            <strong>OPTIMHUB</strong> (SASU au capital de 5&nbsp;001&nbsp;€), immatriculée au RCS de
            Paris sous le numéro 534&nbsp;057&nbsp;088, dont le siège social est situé au 11 rue de
            Lourmel, 75015 Paris.
          </p>
          <p>
            Contact&nbsp;:{" "}
            <a href="mailto:contact@recette-diabete.com" className="text-[hsl(var(--brand-primary))] underline">
              contact@recette-diabete.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">2. Données collectées</h2>
          <p>Nous collectons les données suivantes&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Newsletter</strong>&nbsp;: adresse email (fournie volontairement via le
              formulaire d'inscription).
            </li>
            <li>
              <strong>Données de navigation</strong>&nbsp;: pages visitées, durée de visite,
              provenance, type d'appareil et de navigateur, adresse IP anonymisée (via Google
              Analytics).
            </li>
            <li>
              <strong>Cookies techniques</strong>&nbsp;: nécessaires au bon fonctionnement du Site
              (préférences, session).
            </li>
          </ul>
          <p>
            <strong>Aucune donnée de santé n'est collectée</strong> sur le Site. Les recettes et
            guides ne nécessitent pas de saisie d'informations médicales.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">3. Finalités du traitement</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Envoi de la newsletter (recettes, nouveautés) — base légale&nbsp;: consentement.</li>
            <li>Mesure d'audience et amélioration du Site — base légale&nbsp;: intérêt légitime / consentement pour les cookies non essentiels.</li>
            <li>Assurer la sécurité et prévenir les abus techniques — base légale&nbsp;: intérêt légitime.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">4. Durée de conservation</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Newsletter&nbsp;: jusqu'à demande de désinscription de l'utilisateur.</li>
            <li>Données Google Analytics&nbsp;: 14 mois (paramétrage par défaut).</li>
            <li>Logs techniques&nbsp;: 12 mois maximum.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">5. Cookies et traceurs</h2>
          <p>Le Site utilise deux types de cookies&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Cookies techniques essentiels</strong>&nbsp;: nécessaires au fonctionnement du
              Site (session, session admin). Ils ne requièrent pas de consentement.
            </li>
            <li>
              <strong>Cookies de mesure d'audience</strong>&nbsp;: Google Analytics (G-X6W8L1SRZS)
              collecte des données anonymisées pour comprendre l'usage du Site. Vous pouvez refuser
              ces cookies via les paramètres de votre navigateur ou l'extension{" "}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--brand-primary))] underline"
              >
                Google Analytics Opt-out
              </a>.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">6. Destinataires des données</h2>
          <p>Vos données ne sont ni vendues, ni louées, ni cédées à des tiers à des fins commerciales. Elles peuvent être transmises&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>à nos sous-traitants techniques (hébergement Emergent / Cloudflare)&nbsp;;</li>
            <li>à Google (Google Analytics) pour la mesure d'audience — <em>Google est certifié DPF (Data Privacy Framework) et applique les Clauses Contractuelles Types</em>&nbsp;;</li>
            <li>aux autorités compétentes en cas de réquisition judiciaire.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">7. Transferts hors UE</h2>
          <p>
            Certains sous-traitants (Cloudflare, Google) peuvent traiter des données depuis les
            États-Unis. Ces transferts sont encadrés par le <strong>Data Privacy Framework (DPF)</strong>
            {" "}et/ou les Clauses Contractuelles Types de la Commission Européenne, garantissant un
            niveau de protection conforme au RGPD.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">8. Vos droits</h2>
          <p>Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Droit d'accès</strong>&nbsp;: obtenir la confirmation que vos données sont traitées et en recevoir une copie.</li>
            <li><strong>Droit de rectification</strong>&nbsp;: faire corriger des données inexactes.</li>
            <li><strong>Droit à l'effacement</strong> (« droit à l'oubli »)&nbsp;: demander la suppression de vos données.</li>
            <li><strong>Droit à la limitation</strong> du traitement.</li>
            <li><strong>Droit à la portabilité</strong> de vos données.</li>
            <li><strong>Droit d'opposition</strong> au traitement fondé sur l'intérêt légitime.</li>
            <li><strong>Droit de retirer votre consentement</strong> à tout moment (notamment pour la newsletter).</li>
          </ul>
          <p>
            Pour exercer ces droits, écrivez-nous à{" "}
            <a href="mailto:contact@recette-diabete.com" className="text-[hsl(var(--brand-primary))] underline">
              contact@recette-diabete.com
            </a>
            . Nous répondrons dans un délai maximum d'un mois.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">9. Réclamation auprès de la CNIL</h2>
          <p>
            En cas de désaccord persistant, vous avez la possibilité d'introduire une réclamation
            auprès de la <strong>Commission Nationale de l'Informatique et des Libertés (CNIL)</strong>{" "}
            —{" "}
            <a
              href="https://www.cnil.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--brand-primary))] underline"
            >
              www.cnil.fr
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[hsl(var(--text-primary))] mb-3">10. Sécurité</h2>
          <p>
            OPTIMHUB met en œuvre des mesures techniques et organisationnelles appropriées pour
            garantir la sécurité des données personnelles collectées&nbsp;: chiffrement HTTPS,
            authentification renforcée pour l'espace administrateur (JWT), hébergement chez des
            prestataires certifiés.
          </p>
        </section>
      </div>
    </div>
  );
}
